import { useEffect, useState } from 'react';
import axios from 'axios';
import { apiEndpoint } from '../constants/api';
import { JobCandidateGenderChart } from '../charts/JobCandidateGenderChart';
import { JobCandidateVeteranChart } from '../charts/JobCandidateVeteranChart';
import { JobCandidateLgbtqChart } from '../charts/JobCandidateLgbtqChart';
import { JobCandidateEthnicityChart } from '../charts/JobCandidateEthnicityChart';
import Spinner from './Spinner';
import CandidateGridView from './CandidateGridView';
import { Pagination } from '@mui/material';
import { Dropdown } from 'flowbite-react';

const pageSizeOptions = [9, 18, 36, 'All'];

export default function JobCandidateTable({ jobId, jobData }) {
  const [loading, setLoading] = useState(false);
  const [genderChartData, setGenderChartData] = useState([]);
  const [veteranChartData, setVeteranChartData] = useState([]);
  const [lgbtqChartData, setLgbtqChartData] = useState([]);
  const [ethnicityChartData, setEthnicityChartData] = useState([]);
  const [candidateData, setCandidateData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [candidatesPerPage, setCandidatesPerPage] = useState(18);

  useEffect(() => {
    if (jobId) loadJobCandidates();
  }, []);

  const loadJobCandidates = async () => {
    setLoading(true);
    const { data } = await axios.post(
      apiEndpoint(`/admin/job/candidates/${jobId}`)
    );
    setCandidateData(data);
    setGenderChartData(
      data.map((d) => ({ gender: d.gender, count: 1, id: d.user_id }))
    );
    setVeteranChartData(
      data.map((d) => ({ veteran: d.veteran, count: 1, id: d.user_id }))
    );
    setLgbtqChartData(
      data.map((d) => ({ lgbtq: d.lgbtq, count: 1, id: d.user_id }))
    );
    setEthnicityChartData(
      data.flatMap((d) =>
        d.ethnicity.map((eth) => ({
          ethnicity: eth.ethnicity,
          count: 1,
          id: d.user_id,
        }))
      )
    );
    setLoading(false);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Paginate candidates
  const handleCandidatesPerPageChange = (value) => {
    setCandidatesPerPage(value === 'All' ? candidateData.length : value);
    setCurrentPage(1); // Reset to the first page when candidates per page changes
  };

  // Paginate candidates
  const totalPages = Math.ceil(candidateData.length / candidatesPerPage);
  const paginatedCandidates = candidateData.slice(
    (currentPage - 1) * candidatesPerPage,
    currentPage * candidatesPerPage
  );

  return (
    <>
      {loading && <Spinner />}
      {candidateData.length && candidateData.length > 0 ? (
        <>
          <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
            <div className="flex" style={{ height: '250px' }}>
              <div className="flex-auto">
                <JobCandidateGenderChart data={genderChartData} />
              </div>
              <div className="flex-auto">
                <JobCandidateVeteranChart data={veteranChartData} />
              </div>
              <div className="flex-auto">
                <JobCandidateLgbtqChart data={lgbtqChartData} />
              </div>
            </div>
            {ethnicityChartData.length ? (
              <div className="flex" style={{ height: '225px' }}>
                <div className="flex-auto">
                  <JobCandidateEthnicityChart data={ethnicityChartData} />
                </div>
              </div>
            ) : (
              <></>
            )}
          </div>
          <div className="flex  mb-5 mt-5 justify-between items-center">
            <p className="text-gray-700">
              Showing {paginatedCandidates.length} of {candidateData.length}{' '}
              candidates
            </p>
            <Dropdown
              style={{ background: '#f0f5f7', color: 'black' }}
              label={`${
                candidatesPerPage >= candidateData.length
                  ? 'All'
                  : `${candidatesPerPage} Per Page`
              }`}
            >
              {pageSizeOptions.map((option) => (
                <Dropdown.Item
                  key={option}
                  onClick={() => handleCandidatesPerPageChange(option)}
                >
                  {option === 'All' ? 'Show All' : `${option} per page`}
                </Dropdown.Item>
              ))}
            </Dropdown>
          </div>

          {/* Candidate grid view */}
          <div className="mt-5">
            <CandidateGridView
              candidateData={paginatedCandidates}
              jobData={jobData}
            />
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-5">
              <Pagination
                count={totalPages} // Total pages
                page={currentPage} // Current page
                onChange={handlePageChange} // Handle page change
                color="primary"
              />
            </div>
          )}
        </>
      ) : (
        <></>
      )}
    </>
  );
}
